import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import { useUser } from '@/app/model/stores';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { createLogger } from '@/shared/lib/logger';
import { payloadString } from '@/shared/api/websocket/realtime-sync.payload';

const logger = createLogger('useConceptWebSocket');

interface UseConceptWebSocketProps {
  projectId: string;
  conceptId: string | undefined;
  onRefetch: () => void;
}

export function useConceptWebSocket({
  projectId,
  conceptId,
  onRefetch,
}: UseConceptWebSocketProps) {
  const user = useUser();
  const queryClient = useQueryClient();

  // Listen for concept created events
  useEffect(() => {
    if (!user) return;

    const cleanup = onInstant('concepts', (event) => {
      if (event.action !== 'created') return;

      const payload = event.payload;
      if (payloadString(payload, 'project_id') === projectId) {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.concepts.list(projectId),
        });
        if (conceptId) {
          queryClient.invalidateQueries({
            queryKey: cacheKeys.concepts.detail(conceptId),
          });
        }
        onRefetch();
      }
    });

    return cleanup;
  }, [user, projectId, conceptId, queryClient, onRefetch]);

  // Listen for render completed events
  // Always refetch to update settings_render (render settings for the form panel)
  useEffect(() => {
    if (!user) return;

    const cleanup = onInstant('renders', (event) => {
      if (event.action !== 'completed') return;

      if (event.id) {
        logger.info('render completed, refetching concept data');
        onRefetch();
      }
    });

    return cleanup;
  }, [user, conceptId, onRefetch]);
}
